import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLightbulb,
  faLayerGroup,
  faCheckDouble,
  faImage,
  faFileExport,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';

import type { GenerationProgress as ProgressType } from '@/types/presentation';
import styles from './GenerationProgress.module.css';

// ── Phase config ─────────────────────────────────────────
//
// The backend (providers/tasks.py write_phase) writes granular phase
// names at each boundary, plus older legacy names from earlier rounds
// of the renderer. We map every known name to one of five coarse UI
// groups so the stepper always lights up the right step regardless of
// which phase the task is actually in.
//
// Unknown phase names fall back to the first step so the UI at least
// shows "Planning..." instead of "Working...".

const PHASES = [
  {
    key: 'planning',
    label: 'Planning',
    detailLabel: 'Planning content structure',
    icon: faLightbulb,
    // queued/rewrite/plan — Phase 0 and A of the deck pipeline
    // designing_brief — invitation pipeline
    // rendering planning — render pipeline Claude call
    matches: ['queued', 'rewrite', 'plan', 'planning', 'designing_brief'],
  },
  {
    key: 'generating_deck',
    label: 'Slides',
    detailLabel: 'Creating slide content',
    icon: faLayerGroup,
    matches: ['generate_deck', 'generating_deck'],
  },
  {
    key: 'quality_check',
    label: 'Reviewing',
    detailLabel: 'Reviewing quality',
    icon: faCheckDouble,
    matches: ['validate', 'fix', 'quality_check'],
  },
  {
    key: 'images',
    label: 'Images',
    detailLabel: 'Generating images',
    icon: faImage,
    // images_wave1/2/3 are the new per-wave names; legacy: images
    // rendering_image — invitation; rendering — render batch
    matches: [
      'images',
      'images_wave1',
      'images_wave2',
      'images_wave3',
      'rendering_image',
      'rendering',
    ],
  },
  {
    key: 'building_pptx',
    label: 'Finishing',
    detailLabel: 'Building presentation',
    icon: faFileExport,
    // build_pptx + upload_pptx are the new names; building_pptx is legacy
    // done = final state, still maps to this group for the check mark
    matches: ['build_pptx', 'upload_pptx', 'building_pptx', 'done', 'completed'],
  },
] as const;

type PhaseKey = (typeof PHASES)[number]['key'];

function phaseIndex(phase: string, wizardStep?: number): number {
  if (wizardStep && wizardStep >= 1 && wizardStep <= 5) {
    return Math.min(wizardStep - 1, PHASES.length - 1);
  }
  if (!phase) return 0;
  const normalized = phase.toLowerCase();
  const idx = PHASES.findIndex((p) => (p.matches as readonly string[]).includes(normalized));
  return idx >= 0 ? idx : 0;
}

export function getPhaseLabel(phase: string, wizardStep?: number): string {
  const idx = phaseIndex(phase, wizardStep);
  return PHASES[idx]?.label ?? 'Working';
}

// ── Props ────────────────────────────────────────────────

interface GenerationProgressProps {
  progress: ProgressType | null | undefined;
  mode: 'compact' | 'detailed' | 'mini';
}

// ── Component ────────────────────────────────────────────

/**
 * Returns true when the current phase belongs to the "images" UI group —
 * which includes all three granular wave names (images_wave1/2/3) plus
 * legacy names (images, rendering, rendering_image). Used by sub-components
 * to decide whether to show the numeric N/M image counter.
 */
function isImagesPhase(phase: string, wizardStep?: number): boolean {
  return phaseIndex(phase, wizardStep) === 3;
}

export function GenerationProgress({ progress, mode }: GenerationProgressProps) {
  if (!progress) return null;

  const currentIdx = phaseIndex(progress.phase, progress.wizard_step);

  if (mode === 'mini') {
    return <MiniProgress currentIdx={currentIdx} progress={progress} />;
  }

  if (mode === 'compact') {
    return <CompactProgress phase={progress.phase as PhaseKey} currentIdx={currentIdx} progress={progress} />;
  }

  return <DetailedProgress phase={progress.phase as PhaseKey} currentIdx={currentIdx} progress={progress} />;
}

// ── Mini mode (list page — below badge) ──────────────────

function MiniProgress({
  currentIdx,
  progress,
}: {
  currentIdx: number;
  progress: ProgressType;
}) {
  const isImages = isImagesPhase(progress.phase, progress.wizard_step) && progress.total_images > 0;

  return (
    <div className={styles.mini}>
      <div className={styles.miniSteps}>
        {PHASES.map((p, idx) => {
          const state = idx < currentIdx ? 'done' : idx === currentIdx ? 'active' : 'pending';
          const isLast = idx === PHASES.length - 1;
          return (
            <div key={p.key} className={styles.miniStepGroup}>
              <div className={`${styles.miniCircle} ${styles[`miniCircle_${state}`]}`}>
                {state === 'done' ? (
                  <FontAwesomeIcon icon={faCheck} className={styles.miniCheckIcon} />
                ) : (
                  <span className={styles.miniNum}>{idx + 1}</span>
                )}
              </div>
              {!isLast && (
                <div className={`${styles.miniLine} ${idx < currentIdx ? styles.miniLine_done : ''}`} />
              )}
            </div>
          );
        })}
      </div>
      {isImages && (
        <span className={styles.miniImageCount}>
          {progress.completed_images}/{progress.total_images}
        </span>
      )}
    </div>
  );
}

// ── Compact mode (list page) ─────────────────────────────

function CompactProgress({
  phase,
  currentIdx,
  progress,
}: {
  phase: PhaseKey;
  currentIdx: number;
  progress: ProgressType;
}) {
  const cfg = PHASES[currentIdx];
  const isImages = isImagesPhase(phase, progress.wizard_step) && progress.total_images > 0;
  const pct = isImages ? Math.round((progress.completed_images / progress.total_images) * 100) : 0;

  return (
    <span className={styles.compact}>
      <span className={styles.compactDot} />
      <span className={styles.compactLabel}>
        {isImages ? `Images ${progress.completed_images}/${progress.total_images}` : `${cfg?.label ?? 'Working'}...`}
      </span>
      <span className={styles.compactBar}>
        <span
          className={`${styles.compactFill} ${isImages ? '' : styles.compactIndeterminate}`}
          style={isImages ? { width: `${pct}%` } : undefined}
        />
      </span>
    </span>
  );
}

// ── Phase-specific content constants ─────────────────────

const PLAN_STEPS = [
  'Analyzing your event requirements',
  'Reviewing selected providers and services',
  'Designing the presentation structure',
  'Mapping content to slide sections',
  'Selecting layout styles and visuals',
  'Crafting the narrative flow',
];

const SLIDE_MESSAGES = [
  'Writing your executive overview...',
  'Crafting the event concept section...',
  'Building provider highlights...',
  'Adding service breakdowns...',
  'Creating cost comparison tables...',
  'Composing logistics overview...',
  'Designing the closing section...',
];

const IMAGE_LOADING_LABELS = [
  'Calling generation API',
  'Considering prompt',
  'Adding brand elements',
  'Finalizing',
];

// ── Detailed mode (detail page) ──────────────────────────

function DetailedProgress({
  phase,
  currentIdx,
  progress,
}: {
  phase: PhaseKey;
  currentIdx: number;
  progress: ProgressType;
}) {
  // Planning: auto-advance checklist every 3.5 s, reset on phase change
  const [planStep, setPlanStep] = useState(0);
  useEffect(() => {
    setPlanStep(0);
    if (currentIdx !== 0) return;
    const id = setInterval(() => setPlanStep((s) => Math.min(s + 1, PLAN_STEPS.length - 1)), 3500);
    return () => clearInterval(id);
  }, [currentIdx]);

  // Slides: cycle message every 7 s + count elapsed seconds, reset on phase change
  const [slideMsg, setSlideMsg] = useState(0);
  const [slideElapsedSec, setSlideElapsedSec] = useState(0);
  useEffect(() => {
    setSlideMsg(0);
    setSlideElapsedSec(0);
    if (currentIdx !== 1) return;
    const msgId = setInterval(() => setSlideMsg((s) => (s + 1) % SLIDE_MESSAGES.length), 7000);
    const elId = setInterval(() => setSlideElapsedSec((s) => s + 1), 1000);
    return () => {
      clearInterval(msgId);
      clearInterval(elId);
    };
  }, [currentIdx]);

  // Images: cycle active-image status label every 2.2 s
  const [imgLabel, setImgLabel] = useState(0);
  useEffect(() => {
    setImgLabel(0);
    if (currentIdx !== 3) return;
    const id = setInterval(() => setImgLabel((s) => (s + 1) % IMAGE_LOADING_LABELS.length), 2200);
    return () => clearInterval(id);
  }, [currentIdx]);

  const isImages = isImagesPhase(phase, progress.wizard_step) && progress.total_images > 0;
  const imgPct = isImages
    ? Math.round((progress.completed_images / progress.total_images) * 100)
    : 0;
  // Fake slides progress: fills over ~55 s, capped at 92 % (we can't know exact finish time)
  const slidesPct = Math.min(Math.round((slideElapsedSec / 55) * 100), 92);
  const slidesTimerMins = Math.floor(slideElapsedSec / 60);
  const slidesTimerSecs = String(slideElapsedSec % 60).padStart(2, '0');

  return (
    <div className={styles.detailed}>
      {/* ── Stepper ── */}
      <div className={styles.stepper}>
        {PHASES.map((p, idx) => {
          const state = idx < currentIdx ? 'completed' : idx === currentIdx ? 'active' : 'pending';
          const isLast = idx === PHASES.length - 1;
          return (
            <div key={p.key} className={styles.stepGroup}>
              <div className={`${styles.step} ${styles[`step_${state}`]}`}>
                <div className={styles.stepCircle}>
                  {state === 'completed' ? (
                    <FontAwesomeIcon icon={faCheck} className={styles.stepCheckIcon} />
                  ) : (
                    <FontAwesomeIcon icon={p.icon} className={styles.stepIcon} />
                  )}
                </div>
                <span className={styles.stepLabel}>{p.label}</span>
              </div>
              {!isLast && (
                <div
                  className={`${styles.connector} ${
                    idx < currentIdx ? styles.connector_completed : styles.connector_pending
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* ── Phase 0: Planning ── animated checklist */}
      {currentIdx === 0 && (
        <div className={styles.planDetail}>
          <p className={styles.activeLabel}>Planning content structure</p>
          <div className={styles.planSteps}>
            {PLAN_STEPS.map((text, i) => {
              const s = i < planStep ? 'done' : i === planStep ? 'active' : 'pending';
              return (
                <div key={i} className={`${styles.planStep} ${styles[`planStep_${s}`]}`}>
                  <span className={styles.planIcon}>
                    {s === 'done' ? '✓' : s === 'active' ? '›' : '·'}
                  </span>
                  <span>{text}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Phase 1: Slides ── cycling message + elapsed progress */}
      {currentIdx === 1 && (
        <div className={styles.slidesDetail}>
          <div className={styles.slidesMsgRow}>
            <p className={styles.activeLabel} style={{ margin: 0 }}>Creating slide content</p>
            <span className={styles.slidesTimer}>
              {slidesTimerMins}:{slidesTimerSecs}
            </span>
          </div>
          <p className={styles.slidesMsg} key={slideMsg}>
            {SLIDE_MESSAGES[slideMsg]}
          </p>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${slidesPct}%` }} />
          </div>
          <p className={styles.slidesHint}>This step usually takes about a minute</p>
        </div>
      )}

      {/* ── Phase 2 / 4: Reviewing + Finishing ── simple indeterminate */}
      {(currentIdx === 2 || currentIdx === 4) && (
        <div className={styles.activeDetail}>
          <p className={styles.activeLabel}>
            {currentIdx === 2
              ? 'Reviewing quality and structure...'
              : 'Building and uploading your presentation...'}
          </p>
          <div className={styles.progressBar}>
            <div className={`${styles.progressFill} ${styles.progressIndeterminate}`} />
          </div>
        </div>
      )}

      {/* ── Phase 3: Images ── real % bar + cycling active label */}
      {currentIdx === 3 && (
        <div className={styles.imagesDetail}>
          <div className={styles.imagesMeta}>
            <p className={styles.activeLabel} style={{ margin: 0 }}>Generating images</p>
            {isImages && (
              <span className={styles.imageCount}>
                {progress.completed_images} / {progress.total_images}
              </span>
            )}
          </div>
          <div className={styles.progressBar}>
            <div
              className={`${styles.progressFill} ${isImages ? '' : styles.progressIndeterminate}`}
              style={isImages ? { width: `${imgPct}%` } : undefined}
            />
          </div>
          {isImages && (
            <div className={styles.imgActiveRow}>
              <span className={styles.imgActiveDot} />
              <span className={styles.imgActiveLabel}>{IMAGE_LOADING_LABELS[imgLabel]}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
